import { createSelector } from 'reselect'
import { isCruiseV1, isFlightItem, isInstantBookingHotelItem, isInsuranceItem, isInsuranceProtectionItem, isNoProtectionInsuranceBookingProtectionItem, isNoProtectionInsuranceItem } from 'lib/checkout/checkoutUtils'
import { findInsuranceUpgradeSourceItem } from 'lib/insurance/insuranceHelpers'
import { reselectShallowEqual } from 'lib/selector/selectorUtils'
import { getAccommodationItems } from './accommodation'
import { getFlightItems } from './flights'
import { isEnabledForFeature } from 'lib/config/featureFlagUtils'
import { CHECKOUT_ITEM_TYPE_CRUISE, CHECKOUT_ITEM_TYPE_TOUR_V1 } from 'constants/checkout'
import config from 'constants/config'
import getBedbankItems from './items/getBedbankItems'
import getAllInsuranceBookingProtectionItems from './items/getAllInsuranceBookingProtectionItems'

export const getInsuranceItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.InsuranceItem> => items.filter(isInsuranceProtectionItem),
  reselectShallowEqual,
)

export const getNoProtectionInsuranceItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.InsuranceItem> => items.filter(isNoProtectionInsuranceItem),
  reselectShallowEqual,
)

export const getNoProtectionInsuranceBookingProtectionItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.InsuranceItem> => items.filter(isNoProtectionInsuranceBookingProtectionItem),
  reselectShallowEqual,
)

// It includes the no-protection insurance item
export const getAllInsuranceItems = createSelector(
  (state: App.State) => state.checkout.cart.items,
  (items): Array<App.Checkout.InsuranceItem> => items.filter(isInsuranceItem),
  reselectShallowEqual,
)

export const getInsuranceUpgradeSourceItem = createSelector(
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => state.checkout.cart.existingOrder?.insuranceItems,
  (existingOrder, insuranceItems) => {
    const activeInsuranceItem = insuranceItems?.find(item => item.status === 'completed')

    if (!activeInsuranceItem || !existingOrder) {
      return null
    }

    const insuranceUpgradeSourceItem = findInsuranceUpgradeSourceItem(activeInsuranceItem, existingOrder)
    return insuranceUpgradeSourceItem
  },
)

export const isCheckoutPostPurchaseInsurance = createSelector(
  (state: App.State) => state.checkout.cart.mode,
  (checkoutMode) => checkoutMode === 'insurance',
)

export const getInsuranceEnabledCheckoutItems = createSelector(
  (state: App.State) => getAccommodationItems(state),
  (state: App.State) => getFlightItems(state),
  (state: App.State) => state.offer.offers,
  (items, flighItems, leOffers): Array<App.Checkout.InstantBookingItem | App.Checkout.FlightItem> => {
    return [...items, ...flighItems]
      .filter(item => isInstantBookingHotelItem(item) || isFlightItem(item))
      .filter(item => isEnabledForFeature(config.UNIVERSAL_CHECKOUT_INSURANCE_ENABLED_LIST, item.itemType))
      .filter(item => {
        if (item.itemType === CHECKOUT_ITEM_TYPE_TOUR_V1 || item.itemType === CHECKOUT_ITEM_TYPE_CRUISE) {
          return isCruiseV1(item, leOffers)
        }
        return true
      })
  },
)

export const checkoutContainsBedbankWithCFMRFlexPackage = createSelector(
  (state: App.State) => getBedbankItems(state),
  (state: App.State) => getAllInsuranceBookingProtectionItems(state),
  (bedbankItems, bookingProtectionItems): boolean => {
    return bookingProtectionItems.some(item => !!item.bedbankRoomIds?.length || !!item.isAccommodationBundle)
  }
)
